import React, { useState } from 'react';
import { 
  Repeat, ExternalLink, Shield, TrendingUp, Clock, 
  Zap, CheckCircle2, ArrowRight, Coins, Wallet, Send 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UEX_REFERRAL } from '../constants';

// UEX Swap base URL with referral
const UEX_SWAP_BASE = "https://uexswap.com";
const UEX_REFERRAL_PARAM = "?ref=5jcsh81dhaos";

export const ExchangePage = () => {
  const [activeTab, setActiveTab] = useState("swap");
  const [selectedPair, setSelectedPair] = useState("ADA-BURNZ");
  
  // Quick swap pairs focused on ADA and BURNZ
  const quickSwapPairs = [
    { id: "ADA-BURNZ", from: "ADA", to: "BURNZ", fromNetwork: "CARDANO", toNetwork: "CARDANO", isBurnz: true },
    { id: "BURNZ-ADA", from: "BURNZ", to: "ADA", fromNetwork: "CARDANO", toNetwork: "CARDANO", isBurnz: true },
    { id: "SOL-ADA", from: "SOL", to: "ADA", fromNetwork: "SOLANA", toNetwork: "CARDANO" },
    { id: "ETH-ADA", from: "ETH", to: "ADA", fromNetwork: "ERC20", toNetwork: "CARDANO" },
    { id: "BTC-ADA", from: "BTC", to: "ADA", fromNetwork: "BTC", toNetwork: "CARDANO" },
    { id: "USDT-ADA", from: "USDT", to: "ADA", fromNetwork: "TRC20", toNetwork: "CARDANO" },
    { id: "ADA-SOL", from: "ADA", to: "SOL", fromNetwork: "CARDANO", toNetwork: "SOLANA" },
    { id: "ADA-ETH", from: "ADA", to: "ETH", fromNetwork: "CARDANO", toNetwork: "ERC20" },
  ];

  const getSwapUrl = (pair) => {
    const pairData = quickSwapPairs.find(p => p.id === pair);
    if (pairData) {
      if (pairData.isBurnz) {
        return `${UEX_SWAP_BASE}/exchange/ADA-USDT/CARDANO-TRC20${UEX_REFERRAL_PARAM}`;
      }
      return `${UEX_SWAP_BASE}/exchange/${pairData.from}-${pairData.to}/${pairData.fromNetwork}-${pairData.toNetwork}${UEX_REFERRAL_PARAM}`;
    }
    return `${UEX_SWAP_BASE}/exchange/ADA-USDT/CARDANO-TRC20${UEX_REFERRAL_PARAM}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-secondary-theme flex items-center justify-center">
            <Repeat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Exchange</h1>
            <p className="text-text-secondary text-sm">Swap crypto instantly with UEX Swap</p>
          </div>
        </div>
        <a 
          href={`${UEX_SWAP_BASE}/exchange/all${UEX_REFERRAL_PARAM}`}
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          <span className="text-sm">View All Pairs</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* UEX Sign Up CTA */}
      <Card className="bg-gradient-to-r from-cyan-600/20 via-cyan-500/10 to-blue-600/20 border-cyan-500/30">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500 flex items-center justify-center">
              <Coins className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold">New to UEX? Sign up now!</h3>
              <p className="text-cyan-400/70 text-sm">Create an account to trade BURNZ and 2000+ cryptocurrencies</p>
            </div>
          </div>
          <a 
            href={UEX_REFERRAL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white">
              <ExternalLink className="w-4 h-4 mr-2" />
              Sign Up on UEX.us
            </Button>
          </a>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-cyan-500/10 to-secondary-theme/10 border-cyan-500/20">
        <CardContent className="p-4 flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-cyan-400 font-semibold mb-1">Secure & Fast Swaps</h3>
            <p className="text-text-secondary text-sm">
              Exchange over 2000+ cryptocurrencies instantly. No registration required. 
              <span className="text-cyan-400"> Powered by UEX Swap</span> - a secure, all-in-one payment solution.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-1">
        {[
          { id: "swap", label: "Swap", icon: Repeat },
          { id: "pairs", label: "Popular Pairs", icon: TrendingUp },
          { id: "history", label: "History", icon: Clock },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
              activeTab === tab.id 
                ? "text-cyan-400 bg-cyan-500/10 border-b-2 border-cyan-500" 
                : "text-text-secondary hover:text-white hover:bg-white/5"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Swap Tab */}
      {activeTab === "swap" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Quick Pair Selection */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-white font-medium">Quick Select</h3>
            <div className="space-y-2">
              {quickSwapPairs.map(pair => (
                <button
                  key={pair.id}
                  onClick={() => setSelectedPair(pair.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                    selectedPair === pair.id 
                      ? "bg-cyan-500/20 border border-cyan-500/30" 
                      : "bg-surface-2 border border-white/5 hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center -space-x-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 border-surface-2 z-10 ${
                      pair.from === "BURNZ" ? "bg-primary-theme/30" : 
                      pair.from === "ADA" ? "bg-blue-500/20" : "bg-secondary-theme/20"
                    }`}>
                      <span className={`text-xs font-bold ${
                        pair.from === "BURNZ" ? "text-primary-theme" : 
                        pair.from === "ADA" ? "text-blue-400" : "text-secondary-theme"
                      }`}>
                        {pair.from === "BURNZ" ? "🔥" : pair.from.slice(0, 2)}
                      </span>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 border-surface-2 ${
                      pair.to === "BURNZ" ? "bg-primary-theme/30" : 
                      pair.to === "ADA" ? "bg-blue-500/20" : "bg-green-500/20"
                    }`}>
                      <span className={`text-xs font-bold ${
                        pair.to === "BURNZ" ? "text-primary-theme" : 
                        pair.to === "ADA" ? "text-blue-400" : "text-green-400"
                      }`}>
                        {pair.to === "BURNZ" ? "🔥" : pair.to.slice(0, 2)}
                      </span>
                    </div>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-white text-sm font-medium">{pair.from} → {pair.to}</p>
                    <p className="text-text-muted text-xs">{pair.isBurnz ? "Cardano Native" : pair.fromNetwork}</p>
                  </div>
                  {pair.isBurnz && <Badge className="bg-primary-theme/20 text-primary-theme border-0 text-[10px]">BURNZ</Badge>}
                  {selectedPair === pair.id && (
                    <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* UEX Swap Iframe */}
          <div className="lg:col-span-3">
            <Card className="bg-surface-2 border-white/10 overflow-hidden">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <img 
                    src="https://uexswap.com/build/assets/swap-logo-qwhFI2xs.png" 
                    alt="UEX Swap" 
                    className="h-6"
                  />
                  <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">Live</Badge>
                </div>
                <a 
                  href={getSwapUrl(selectedPair)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-white text-xs flex items-center gap-1"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
              </CardHeader>
              <CardContent className="p-0">
                <iframe
                  src={getSwapUrl(selectedPair)}
                  title="UEX Swap"
                  className="w-full h-[600px] border-0"
                  allow="clipboard-read; clipboard-write"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Popular Pairs Tab */}
      {activeTab === "pairs" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { from: "ADA", to: "BURNZ", rate: "2.5", change: "+5.45%", isBurnz: true },
            { from: "BURNZ", to: "ADA", rate: "0.40", change: "+5.45%", isBurnz: true },
            { from: "SOL", to: "ADA", rate: "212.50", change: "+3.12%" },
            { from: "ETH", to: "ADA", rate: "3,645.67", change: "+1.23%" },
            { from: "BTC", to: "ADA", rate: "106,728.50", change: "-0.54%" },
            { from: "ADA", to: "SOL", rate: "0.0047", change: "-2.89%" },
            { from: "ADA", to: "ETH", rate: "0.00027", change: "-1.12%" },
            { from: "USDT", to: "ADA", rate: "1.12", change: "+2.45%" },
            { from: "ADA", to: "USDT", rate: "0.89", change: "+2.45%" },
          ].map((pair, i) => (
            <Card 
              key={i} 
              className={`bg-surface-2 border-white/5 hover:border-cyan-500/30 cursor-pointer transition-all ${pair.isBurnz ? 'ring-1 ring-primary-theme/20' : ''}`}
              onClick={() => {
                setSelectedPair(`${pair.from}-${pair.to}`);
                setActiveTab("swap");
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      pair.from === "BURNZ" ? "bg-primary-theme/30" : 
                      pair.from === "ADA" ? "bg-blue-500/20" : "bg-secondary-theme/20"
                    }`}>
                      <span className={`text-sm font-bold ${
                        pair.from === "BURNZ" ? "text-primary-theme" : 
                        pair.from === "ADA" ? "text-blue-400" : "text-secondary-theme"
                      }`}>
                        {pair.from === "BURNZ" ? "🔥" : pair.from.slice(0, 2)}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-text-muted" />
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      pair.to === "BURNZ" ? "bg-primary-theme/30" : 
                      pair.to === "ADA" ? "bg-blue-500/20" : "bg-green-500/20"
                    }`}>
                      <span className={`text-sm font-bold ${
                        pair.to === "BURNZ" ? "text-primary-theme" : 
                        pair.to === "ADA" ? "text-blue-400" : "text-green-400"
                      }`}>
                        {pair.to === "BURNZ" ? "🔥" : pair.to.slice(0, 2)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {pair.isBurnz && <Badge className="bg-primary-theme/20 text-primary-theme border-0 text-[10px]">BURNZ</Badge>}
                    <Badge className={`${pair.change.startsWith('+') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'} border-0 text-xs`}>
                      {pair.change}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{pair.from}/{pair.to}</p>
                  <p className="text-text-secondary text-sm">1 {pair.from} = {pair.rate} {pair.to}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <Card className="bg-surface-2 border-white/10">
          <CardContent className="py-16 text-center">
            <Clock className="w-16 h-16 mx-auto mb-4 text-text-muted" />
            <p className="text-text-secondary text-lg">No swap history yet</p>
            <p className="text-text-muted text-sm mt-2">Your completed swaps will appear here</p>
            <Button 
              onClick={() => setActiveTab("swap")} 
              className="mt-4 bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              Start Swapping
            </Button>
          </CardContent>
        </Card>
      )}

      {/* How It Works */}
      <Card className="bg-surface-2 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            How to Swap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: 1, title: "Select Pair", desc: "Choose what you want to send and receive. Rates update instantly.", icon: Coins },
              { step: 2, title: "Enter Address", desc: "Paste your destination wallet address for the receiving coin.", icon: Wallet },
              { step: 3, title: "Confirm & Send", desc: "Review details, confirm the exchange, and send your crypto.", icon: Send },
            ].map(item => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-400 font-bold">{item.step}</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">{item.title}</h4>
                  <p className="text-text-secondary text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExchangePage;
